import React, { useEffect } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, Share,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { IconButton, Badge, Icon } from 'react-native-paper';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { theme } from '../Common.style';

const MODE_LABELS = {
  LOCAL: 'Local',
  COPRESENT: 'Même endroit',
  REMOTE: 'À distance',
};

export default function GameWaitingRoomScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const { room, currentRound, isHost, currentUserId, startRound, startLocalRound, leaveRoom, localPlayers } = useGame();

  useEffect(() => {
    if (!room) navigation.replace(SCREEN_NAMES.GAME_LOBBY);
  }, [room?.id]);

  useEffect(() => {
    if (!isHost && currentRound?.status === 'ROLE_REVEAL') {
      navigation.navigate(SCREEN_NAMES.GAME_ROLE_REVEAL);
    }
  }, [currentRound?.id, currentRound?.status]);

  if (!room) return null;

  const isLocal = room.mode === 'LOCAL';
  const players = isLocal ? localPlayers : (room.players ?? []);
  const onlineCount = isLocal ? players.length : players.filter((p) => p.online).length;
  const canStart = onlineCount >= 3;

  const handleShare = async () => {
    try {
      await Share.share({
        message: `Rejoins ma partie de Jeu de l'Imposteur ! Code : ${room.code}`,
        title: "Jeu de l'Imposteur",
      });
    } catch {}
  };


  const handleStart = async () => {
    try {
      if (isLocal) {
        startLocalRound();
      } else {
        await startRound();
      }
      navigation.navigate(SCREEN_NAMES.GAME_ROLE_REVEAL);
    } catch (e) {
      toast.show(e.message || 'Erreur au démarrage', { type: 'danger' });
    }
  };

  const handleLeave = () => {
    leaveRoom().catch(() => {});
    navigation.goBack();
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <IconButton icon="close" size={24} onPress={handleLeave} />
        <Text style={styles.headerTitle}>Salle d'attente</Text>
        <View style={{ width: 40 }} />
      </View>

      {!isLocal && (
        <View style={styles.codeBlock}>
          <Text style={styles.codeLabel}>Code de la salle</Text>
          <Text style={styles.code}>{room.code}</Text>
          <View style={styles.codeMeta}>
            <Text style={styles.modeTag}>{MODE_LABELS[room.mode] ?? room.mode}</Text>
            <TouchableOpacity style={styles.shareBtn} onPress={handleShare}>
              <Icon source="share-variant" size={16} color="#fff" />
              <Text style={styles.shareBtnText}>Partager</Text>
            </TouchableOpacity>

          </View>
        </View>
      )}

      {isLocal && (
        <View style={styles.codeBlock}>
          <Text style={styles.codeLabel}>Mode Local — Passez le téléphone</Text>
          <Text style={styles.modeTag}>Local</Text>
        </View>
      )}

      <Text style={styles.playersTitle}>
        Joueurs ({onlineCount}{isLocal ? '' : `/${players.length}`})
      </Text>

      <ScrollView style={styles.list}>
        {isLocal
          ? localPlayers.map((p, i) => (
            <View key={i} style={styles.playerRow}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>{p.name[0].toUpperCase()}</Text>
              </View>
              <Text style={styles.playerName}>{p.name}</Text>
            </View>
          ))
          : players.map((p) => (
            <View key={p.id} style={styles.playerRow}>
              <View style={[styles.avatar, !p.online && styles.avatarOffline]}>
                <Text style={styles.avatarText}>
                  {p.user?.firstName?.[0]?.toUpperCase() ?? '?'}
                </Text>
              </View>
              <Text style={styles.playerName}>{p.user?.firstName ?? 'Joueur'}</Text>
              {p.userId === room.hostId && (
                <Text style={styles.hostBadge}>Hôte</Text>
              )}
              <View style={[styles.dot, p.online ? styles.dotOnline : styles.dotOffline]} />
            </View>
          ))}
      </ScrollView>

      {(isHost || isLocal) && (
        <View style={styles.footer}>
          {!canStart && (
            <Text style={styles.hint}>Minimum 3 joueurs pour démarrer</Text>
          )}
          <TouchableOpacity
            style={[styles.startBtn, !canStart && styles.btnDisabled]}
            onPress={handleStart}
            disabled={!canStart}
          >
            <Text style={styles.startBtnText}>Démarrer la partie</Text>
          </TouchableOpacity>
        </View>
      )}

      {!isHost && !isLocal && (
        <View style={styles.footer}>
          <Text style={styles.waitingText}>En attente que l'hôte démarre…</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, paddingHorizontal: 4 },
  headerTitle: { fontWeight: '700', fontSize: 18 },
  codeBlock: { alignItems: 'center', backgroundColor: theme.colors.primary + '15', margin: 16, borderRadius: 16, padding: 20 },
  codeLabel: { color: '#555', fontSize: 13, marginBottom: 4 },
  code: { fontSize: 48, fontWeight: '900', letterSpacing: 12, color: theme.colors.primary },
  codeMeta: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 12 },
  modeTag: { backgroundColor: theme.colors.primary, color: '#fff', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4, fontSize: 13, fontWeight: '600', overflow: 'hidden' },
  shareBtn: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#555', borderRadius: 20, paddingHorizontal: 12, paddingVertical: 6, gap: 6,
  },
  shareBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  playersTitle: { fontWeight: '700', fontSize: 16, marginHorizontal: 20, marginBottom: 8 },
  list: { flex: 1, paddingHorizontal: 20 },
  playerRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  avatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: theme.colors.primary, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  avatarOffline: { backgroundColor: '#ccc' },
  avatarText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  playerName: { flex: 1, fontSize: 16, color: '#333' },
  hostBadge: { fontSize: 12, color: theme.colors.primary, fontWeight: '600', marginRight: 8 },
  dot: { width: 10, height: 10, borderRadius: 5 },
  dotOnline: { backgroundColor: '#4CAF50' },
  dotOffline: { backgroundColor: '#ccc' },
  footer: { padding: 20, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  hint: { textAlign: 'center', color: '#888', fontSize: 13, marginBottom: 8 },
  waitingText: { textAlign: 'center', color: '#888', fontSize: 15 },
  startBtn: { backgroundColor: theme.colors.primary, borderRadius: 14, paddingVertical: 16, alignItems: 'center' },
  btnDisabled: { opacity: 0.4 },
  startBtnText: { color: '#fff', fontWeight: '700', fontSize: 17 },
});
